/** The four tab panels of the scoring guide (ScoringGuideDialog). */

import type { Dimension } from '@/api/types';
import { AiMarker } from '@/ui/AiMarker';

import { PendingReviewBadge, VerdictPill } from './ideaUi';
import { axisDimensions } from './matrixAxes';
import { QUADRANTS } from './matrixGeometry';

const AXIS_LABEL = { impact: 'Impact', feasibility: 'Feasibility' } as const;

function labels(dimensions: Dimension[]) {
  return dimensions.length > 0 ? dimensions.map((d) => d.label).join(', ') : 'no dimensions yet';
}

export function ScoresPanel({ dimensions }: { dimensions: Dimension[] }) {
  return (
    <>
      <p>
        Research scores every idea from 0 to 100 on each dimension. The total is the weighted average of those scores.
        Admins set the dimensions and their weights in Configuration, and the weights always add up to 100%.
      </p>
      <div className="table-scroll">
        <table className="data compact guide-weights">
          <thead>
            <tr>
              <th scope="col">Dimension</th>
              <th scope="col">What it measures</th>
              <th scope="col">Axis</th>
              <th scope="col" className="num">Weight</th>
            </tr>
          </thead>
          <tbody>
            {dimensions.map((d) => (
              <tr key={d.key}>
                <td className="guide-dim">{d.label}</td>
                <td>{d.description}</td>
                <td className="guide-axis">{d.axis ? AXIS_LABEL[d.axis] : <span className="hint">None</span>}</td>
                <td className="num">{d.weight}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hint">Scores of 80 or more are strong, 60–79 are moderate, and below 60 are weak.</p>
    </>
  );
}

export function MatrixPanel({ dimensions, threshold }: { dimensions: Dimension[]; threshold: number }) {
  return (
    <>
      <p>
        The matrix places each idea by two averages of its scores, each dimension counted by its weight. An idea
        appears once research has scored it on both axes.
      </p>
      <dl className="guide-list">
        <dt className="guide-term">Impact</dt>
        <dd>{labels(axisDimensions(dimensions, 'impact'))}</dd>
        <dt className="guide-term">Feasibility</dt>
        <dd>{labels(axisDimensions(dimensions, 'feasibility'))}</dd>
      </dl>
      <p>Both axes split at {threshold}, which gives four quadrants:</p>
      <dl className="guide-list">
        <dt className="guide-term">{QUADRANTS.topRight}</dt>
        <dd>High impact and high feasibility. Pursue first.</dd>
        <dt className="guide-term">{QUADRANTS.topLeft}</dt>
        <dd>Easy to deliver, with a smaller payoff.</dd>
        <dt className="guide-term">{QUADRANTS.bottomRight}</dt>
        <dd>A large payoff that takes more to deliver.</dd>
        <dt className="guide-term">{QUADRANTS.bottomLeft}</dt>
        <dd>Low on both. Revisit if something changes.</dd>
      </dl>
      <p className="hint">Admins choose each dimension's axis and the split in Configuration.</p>
    </>
  );
}

export function VerdictPanel() {
  return (
    <>
      <p>
        Research gives its own recommendation and how confident it is in it. It weighs the whole report with the
        dimension weights in mind, so it is not a cut-off on the total. Changing the weights updates totals at once, but
        a verdict only changes when research runs again.
      </p>
      <dl className="guide-list">
        <dt><VerdictPill verdict="go" /></dt>
        <dd>Worth pursuing now.</dd>
        <dt><VerdictPill verdict="conditional_go" /></dt>
        <dd>Promising, but it depends on conditions the report spells out.</dd>
        <dt><VerdictPill verdict="no_go" /></dt>
        <dd>Not worth pursuing as it stands.</dd>
      </dl>
    </>
  );
}

export function IndustryPanel() {
  return (
    <>
      <p>
        If the person who submits an idea leaves its industry blank, research picks the industry that fits it best. It
        chooses an approved industry first, then an industry already proposed. If none fits, it proposes a new
        industry. For a cross-industry idea, it picks the industry where a first proof of concept would land best.
      </p>
      <dl className="guide-list">
        <dt><AiMarker /></dt>
        <dd>The industry was picked by research, not by a person. Hover over it to see why.</dd>
        <dt><PendingReviewBadge /></dt>
        <dd>Research proposed this new industry. An admin approves or rejects it on Industries.</dd>
        <dt><span className="hint">Suggested: …</span></dt>
        <dd>
          Research picked an industry that has been deactivated, so the idea stays unassigned until an admin
          reactivates it.
        </dd>
      </dl>
      <p className="hint">An industry that a person chose, or that an admin set, is never changed by research.</p>
    </>
  );
}
